/**
 * Horizontal strip showing every action in the hand as a clickable chip.
 * Hero actions carry a verdict badge (good/okay/mistake). Clicking a
 * chip jumps the replay to that frame. The currently-selected step is
 * highlighted.
 */
import type { Action, PlayerId } from '@/engine/types';
import type { Frame } from '@/replay/buildFrames';
import type { GtoDeltaEntry, Verdict } from '@/replay/computeGtoDelta';

export type ActionStripProps = {
  frames: ReadonlyArray<Frame>;
  heroId: PlayerId;
  gtoDelta: ReadonlyArray<GtoDeltaEntry>;
  currentStep: number;
  onSeek: (step: number) => void;
};

const VERDICT_CLASS: Record<Verdict, string> = {
  good: 'bg-success/20 text-success border-success/50',
  okay: 'bg-warning/20 text-warning border-warning/50',
  mistake: 'bg-danger/20 text-danger border-danger/50',
};

function actionLabel(action: Action): string {
  switch (action.type) {
    case 'fold':
      return 'F';
    case 'check':
      return 'K';
    case 'call':
      return `C${action.amount}`;
    case 'bet':
      return `B${action.amount}`;
    case 'raise':
      return `R${action.to}`;
    case 'allin':
      return `Ai`;
  }
}

function describeAction(action: Action): string {
  switch (action.type) {
    case 'fold':
      return '폴드';
    case 'check':
      return '체크';
    case 'call':
      return `콜 ${action.amount}`;
    case 'bet':
      return `벳 ${action.amount}`;
    case 'raise':
      return `레이즈 ${action.to}`;
    case 'allin':
      return `올인 ${action.amount}`;
  }
}

export function ActionStrip({ frames, heroId, gtoDelta, currentStep, onSeek }: ActionStripProps) {
  const deltaByIndex = new Map<number, GtoDeltaEntry>();
  for (const d of gtoDelta) deltaByIndex.set(d.frameIndex, d);

  return (
    <div
      className="flex items-center gap-1 overflow-x-auto px-3 py-2 border-t border-border bg-bg"
      data-testid="action-strip"
    >
      {frames.slice(1).map((frame, idx) => {
        const frameIdx = idx + 1;
        const action = frame.lastAction;
        if (!action) return null;
        const isHero = action.playerId === heroId;
        const delta = deltaByIndex.get(frameIdx);
        const selected = currentStep === frameIdx;
        const verdictClass = delta ? VERDICT_CLASS[delta.verdict] : '';
        const baseClass = isHero
          ? `border ${verdictClass || 'border-border bg-bg-elev text-fg'}`
          : 'border-border bg-bg-elev text-muted border';
        const selectedRing = selected ? 'ring-2 ring-accent' : '';
        const tooltip = delta?.recommended
          ? `${action.playerId}: ${describeAction(action)} · GTO: ${describeAction(delta.recommended)} (${delta.verdict})`
          : `${action.playerId}: ${describeAction(action)}`;
        return (
          <button
            key={frame.step}
            type="button"
            title={tooltip}
            onClick={() => onSeek(frameIdx)}
            className={`shrink-0 rounded-pill px-2 py-1 text-[10px] font-mono ${baseClass} ${selectedRing}`}
            data-testid={`action-chip-${frameIdx}`}
            data-hero={isHero ? 'true' : undefined}
            data-verdict={delta?.verdict}
          >
            {action.playerId}:{actionLabel(action)}
          </button>
        );
      })}
    </div>
  );
}
